import { useSolana } from '@dewicats/connect-button'
import useAuctionManager from '@hooks/useAuctionManager'
import { useMediaQuery } from '@mui/material'
import { PublicKey } from '@solana/web3.js'
import { useCallback, useMemo, useState } from 'react'
import { useAsync } from 'react-async-hook'
import 'react-loading-skeleton/dist/skeleton.css'

const formatDate = (date: Date) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return `${
    monthNames[date.getMonth()]
  } ${date.getDate()}, ${date.getFullYear()}`
}

type DateHeaderPops = {
  disabledNext: boolean
  disabledPrev: boolean
  dateChange: (change: 'prev' | 'next') => void
}

const DateHeader = ({
  disabledNext,
  disabledPrev,
  dateChange,
}: DateHeaderPops) => {
  const { anchorProvider } = useSolana()
  const { currentListing, currentCat, allCats } = useAuctionManager()
  const [currentDate, setCurrentDate] = useState<Date>(null)

  const catIndex = useMemo(() => {
    return allCats?.findIndex((cat) => cat.id === currentCat?.id)
  }, [allCats, currentCat])

  useAsync(async () => {
    if (!currentListing && currentCat && catIndex) {
      if (catIndex > 68) {
        // TODO: This will need to be updated to a more robust solution
        const signatures =
          await anchorProvider.connection.getSignaturesForAddress(
            new PublicKey(currentCat.id),
            {
              limit: 1000,
            },
          )

        // Get last signature
        const lastSignature = signatures[signatures.length - 1]
        const blockTime = lastSignature.blockTime
        setCurrentDate(new Date(blockTime * 1000))
      } else {
        // return Feb 25 2024 date
        setCurrentDate(new Date(2024, 1, 25))
      }
      return
    }

    if (currentListing?.createdAt) {
      setCurrentDate(new Date(currentListing?.createdAt.toNumber() * 1000))
    }
  }, [currentListing, currentCat, catIndex])

  const handleNext = useCallback(() => {
    dateChange('next')
  }, [dateChange])

  const handlePrev = useCallback(() => {
    dateChange('prev')
  }, [dateChange])

  const isMobile = useMediaQuery('(max-width: 1024px)')

  return (
    <div
      className="btn-group items-center ml-0 text-sm font-medium flex-1"
      role="group"
      aria-label="arrow buttons to change dates"
      style={{
        fontSize: isMobile ? '10px' : '14px',
      }}
    >
      <button
        className="btn circle-icon font-bold h-[30px] border-[1px] border-dark-6"
        disabled={disabledPrev}
        onClick={handlePrev}
      >
        ←
      </button>
      <button
        className="btn circle-icon font-bold h-[30px] border-[1px] border-dark-6"
        disabled={disabledNext}
        onClick={handleNext}
      >
        →
      </button>
      {(currentListing?.createdAt && currentDate !== null) ||
      (currentCat && currentDate !== null) ? (
        <span>{formatDate(currentDate)}</span>
      ) : (
        <span className="w-14 h-4 mx-auto rounded bg-dark-7 animate-pulse text-xs" />
      )}
    </div>
  )
}

export default DateHeader
